import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { fmtMoney, fmtNumber } from '../lib/format';
import type { MarginLevel } from '../lib/types';
import { marginOf } from '../lib/profit';
import { Button, Callout, Checkbox, Input, Ltr, useToast } from './ui';
import { MarginBadge } from './Margin';

type Weak = {
  productId: number;
  sku: string;
  name: string;
  price: number;
  cost: number;
  margin: number;
  marginLevel: MarginLevel;
  basePrice: number | null;
  market: { median: number; customers: number } | null;
  suggestedPrice: number;
  perMonth: number;
  impactMonthly: number;
};
type Side = { productId: number; sku: string; name: string; price: number; margin: number | null; marginLevel: MarginLevel | null };
type Switch = { group: string; from: Side; to: Side & { ownPrice: boolean }; perMonth: number; customerSaves: number; impactMonthly: number };
type Carton = {
  pack: { productId: number; sku: string; name: string; price: number; margin: number };
  carton: { productId: number; sku: string; name: string; price: number; packs: number; perPack: number; margin: number; marginLevel: MarginLevel };
  cartonsMonthly: number;
  customerSaves: number;
  impactMonthly: number;
};
type Optimize = {
  customer: { id: number; businessName: string };
  target: number;
  warn: number;
  weak: Weak[];
  checks: Array<{ productId: number; sku: string; name: string; price: number; cost: number; basePrice: number | null; perMonth: number }>;
  strong: Array<{ productId: number; sku: string; name: string; price: number; margin: number; marginLevel: MarginLevel; profitMonthly: number }>;
  switches: Switch[];
  cartons: Carton[];
  potentialMonthly: number;
  priceList: { withCost: number } | null;
};

const qty = (perMonth: number) => Math.max(1, Math.round(perMonth));

/**
 * Suggestions for one customer: raise weak prices to the target, switch brand, move to cartons.
 * Picked suggestions become a draft quote at the new prices ("הכן הצעת מחיר מעודכנת").
 */
export function OptimizePanel({ customerId }: { customerId: number }) {
  const { data } = useQuery({ queryKey: ['profit', 'optimize', customerId], queryFn: () => api<Optimize>(`/api/profit/customers/${customerId}/optimize`) });
  const can = useCan();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [prices, setPrices] = useState<Record<number, string>>({});
  const [pickedWeak, setPickedWeak] = useState<Set<number>>(new Set());
  const [pickedSwitch, setPickedSwitch] = useState<Set<number>>(new Set());
  const [pickedCarton, setPickedCarton] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const priceOf = (w: Weak) => {
    const t = prices[w.productId];
    const n = t == null ? w.suggestedPrice : Number(t.replace(/[₪,\s]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : w.suggestedPrice;
  };
  const picked = pickedWeak.size + pickedSwitch.size + pickedCarton.size;
  const pickedImpact = useMemo(() => {
    if (!data) return 0;
    let s = 0;
    for (const w of data.weak) if (pickedWeak.has(w.productId)) s += (priceOf(w) - w.price) * w.perMonth;
    for (const x of data.switches) if (pickedSwitch.has(x.from.productId)) s += x.impactMonthly;
    for (const c of data.cartons) if (pickedCarton.has(c.pack.productId)) s += c.impactMonthly;
    return Math.round(s);
  }, [data, pickedWeak, pickedSwitch, pickedCarton, prices]);

  if (!data) return <div className="panel panel-body"><span className="spinner" /> מחשב הצעות…</div>;
  const nothing = !data.weak.length && !data.switches.length && !data.cartons.length && !data.checks.length;

  const makeQuote = async () => {
    const products: Array<{ productId: number; quantity: number; price?: number }> = [];
    for (const w of data.weak) if (pickedWeak.has(w.productId)) products.push({ productId: w.productId, quantity: qty(w.perMonth), price: priceOf(w) });
    for (const x of data.switches) if (pickedSwitch.has(x.from.productId)) products.push({ productId: x.to.productId, quantity: qty(x.perMonth), price: x.to.price });
    for (const c of data.cartons) if (pickedCarton.has(c.pack.productId)) products.push({ productId: c.carton.productId, quantity: Math.max(1, c.cartonsMonthly), price: c.carton.price });
    setBusy(true);
    try {
      const r = await api<{ id: number }>('/api/orders', { body: { customerId: data.customer.id, documentType: 'quote', products } });
      qc.invalidateQueries({ queryKey: ['orders'] });
      toast.success('נפתחה טיוטת הצעת מחיר במחירים המעודכנים');
      navigate(`/orders/${r.id}`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (set: Set<number>, setter: (s: Set<number>) => void, id: number, v: boolean) => {
    const n = new Set(set);
    if (v) n.add(id);
    else n.delete(id);
    setter(n);
  };

  return (
    <section className="panel">
      <div className="panel-head row wrap" style={{ gap: 10 }}>
        <h2>הצעות לשיפור הרווחיות</h2>
        {data.potentialMonthly > 0 ? <span className="badge ok">פוטנציאל ~{fmtMoney(data.potentialMonthly, { round: true })} לחודש</span> : null}
        <span className="spacer" />
        <Link to="/profit/substitutes" className="small">קבוצות תחליפים</Link>
      </div>
      <div className="panel-body stack" style={{ gap: 14 }}>
        {nothing ? (
          <p className="muted small" style={{ margin: 0 }}>
            {data.priceList && data.priceList.withCost === 0
              ? 'אין עדיין עלויות למוצרים במחירון של הלקוח — ההצעות יופיעו אחרי שיהיו עלויות.'
              : `אין הצעות — כל מה שהלקוח קנה בשנה האחרונה ביעד הרווחיות (${data.target}%).`}
          </p>
        ) : null}

        {data.checks.length ? (
          <Callout tone="warn" title="מחירים לבדיקה">
            מחירים שנראים כמו מחיר ליחידה בודדת בזמן שהמוצר הוא חבילה (העלות לא מתאימה למחיר), ולכן לא חושבה להם רווחיות:{' '}
            {data.checks.map((c) => `${c.name} (${fmtMoney(c.price)}, עלות ${fmtMoney(c.cost)})`).join(' · ')}. כדאי לקבוע לו מחיר לחבילה במחירון.
          </Callout>
        ) : null}

        {data.weak.length ? (
          <div className="stack" style={{ gap: 6 }}>
            <div className="strong">מוצרים מתחת ליעד — מחיר מוצע שמביא ל-{data.target}%</div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }} />
                    <th>מוצר</th>
                    <th className="num">מחיר היום</th>
                    <th className="num">רווחיות</th>
                    <th className="num hide-sm" title="החציון של המחיר האחרון ששאר הלקוחות שילמו">לקוחות אחרים</th>
                    <th className="num">מחיר מוצע</th>
                    <th className="num hide-sm">בחודש</th>
                    <th className="num">השפעה לחודש</th>
                  </tr>
                </thead>
                <tbody>
                  {data.weak.map((w) => {
                    const p = priceOf(w);
                    const m = marginOf(p, w.cost, data.target, data.warn);
                    return (
                      <tr key={w.productId}>
                        <td><Checkbox checked={pickedWeak.has(w.productId)} onChange={(v) => toggle(pickedWeak, setPickedWeak, w.productId, v)}><span className="sr-only">בחירה</span></Checkbox></td>
                        <td className="primary">{w.name}<span className="sub"><Ltr>{w.sku}</Ltr> · עלות {fmtMoney(w.cost)}</span></td>
                        <td className="num tabular">{fmtMoney(w.price)}</td>
                        <td className="num"><MarginBadge margin={w.margin} level={w.marginLevel} /></td>
                        <td className="num tabular hide-sm">{w.market ? <>{fmtMoney(w.market.median)}<span className="sub">{w.market.customers} לקוחות</span></> : <span className="faint">—</span>}</td>
                        <td className="num">
                          <Input className="price-input" inputMode="decimal" value={prices[w.productId] ?? String(w.suggestedPrice)} onChange={(e) => setPrices({ ...prices, [w.productId]: e.target.value })} aria-label="מחיר מוצע" />
                          <span className="sub"><MarginBadge margin={m.margin} level={m.level} /></span>
                        </td>
                        <td className="num tabular hide-sm">{fmtNumber(w.perMonth)}</td>
                        <td className="num tabular" style={{ color: 'var(--ok)' }}>+{fmtMoney(Math.round((p - w.price) * w.perMonth), { round: true })}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {data.switches.length ? (
          <div className="stack" style={{ gap: 6 }}>
            <div className="strong">החלפת מותג — לא יקר יותר ללקוח, יותר רווח</div>
            <table className="table">
              <tbody>
                {data.switches.map((x) => (
                  <tr key={x.from.productId}>
                    <td style={{ width: 32 }}><Checkbox checked={pickedSwitch.has(x.from.productId)} onChange={(v) => toggle(pickedSwitch, setPickedSwitch, x.from.productId, v)}><span className="sr-only">בחירה</span></Checkbox></td>
                    <td>
                      <div>{x.from.name} <span className="muted">{fmtMoney(x.from.price)}</span> <MarginBadge margin={x.from.margin} level={x.from.marginLevel} /></div>
                      <div>← <strong>{x.to.name}</strong> <span className="muted">{fmtMoney(x.to.price)}{x.to.ownPrice ? ' (המחיר שלו)' : ' (מחיר בסיס)'}</span> <MarginBadge margin={x.to.margin} level={x.to.marginLevel} /></div>
                      <span className="sub">{x.group} · {fmtNumber(x.perMonth)} בחודש{x.customerSaves > 0 ? ` · הלקוח חוסך ${fmtMoney(x.customerSaves, { round: true })} בחודש` : ''}</span>
                    </td>
                    <td className="num tabular" style={{ color: 'var(--ok)' }}>+{fmtMoney(x.impactMonthly, { round: true })} לחודש</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {data.cartons.length ? (
          <div className="stack" style={{ gap: 6 }}>
            <div className="strong">מעבר לארגז — זול יותר לחבילה ללקוח, ביעד לנו</div>
            <table className="table">
              <tbody>
                {data.cartons.map((c) => (
                  <tr key={c.pack.productId}>
                    <td style={{ width: 32 }}><Checkbox checked={pickedCarton.has(c.pack.productId)} onChange={(v) => toggle(pickedCarton, setPickedCarton, c.pack.productId, v)}><span className="sr-only">בחירה</span></Checkbox></td>
                    <td>
                      {c.pack.name} {fmtMoney(c.pack.price)} לחבילה ← {c.carton.name} {fmtMoney(c.carton.price)} ({fmtMoney(c.carton.perPack)} לחבילה) <MarginBadge margin={c.carton.margin} level={c.carton.marginLevel} />
                      <span className="sub">כ-{fmtNumber(c.cartonsMonthly)} ארגזים בחודש · הלקוח חוסך {fmtMoney(c.customerSaves, { round: true })} בחודש</span>
                    </td>
                    <td className="num tabular" style={{ color: c.impactMonthly >= 0 ? 'var(--ok)' : 'var(--muted)' }}>
                      {c.impactMonthly >= 0 ? '+' : ''}{fmtMoney(c.impactMonthly, { round: true })} לחודש
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {data.weak.length || data.switches.length || data.cartons.length ? (
          <div className="row wrap" style={{ gap: 10 }}>
            {can('orders.edit') ? (
              <Button variant="primary" onClick={makeQuote} loading={busy} disabled={!picked}>
                הכן הצעת מחיר מעודכנת{picked ? ` (${picked})` : ''}
              </Button>
            ) : null}
            {picked ? <span className="small muted">השפעה משוערת: <strong style={{ color: 'var(--ok)' }}>+{fmtMoney(pickedImpact, { round: true })} לחודש</strong> · לפי הכמויות שקנה בשנה האחרונה</span> : <span className="small muted">מסמנים הצעות — נפתחת טיוטה בעורך ההזמנות, שם משנים ומפיקים.</span>}
          </div>
        ) : null}

        {data.strong.length ? (
          <div className="small muted">
            <strong>מוצרים חזקים לשמור ולהרחיב:</strong> {data.strong.slice(0, 6).map((s) => `${s.name} (${s.margin}%)`).join(' · ')}
          </div>
        ) : null}
      </div>
    </section>
  );
}
